import { useCallback, useEffect } from 'react';
import { collectPageMatches, pickPreferredIndex, toCompactQuery } from './pdfSearchMatching';
import type { PDFSearchResult, PdfDisplayMode, SearchResult } from './types';
import type { PdfViewerState } from './usePdfViewerState';

interface UsePdfSearchArgs {
  state: PdfViewerState;
  pdfUrl: string;
  displayMode: PdfDisplayMode;
  highlightTerms: string[];
  onSearchResults?: (results: SearchResult[]) => void;
  scrollToMatch: (
    page: number,
    pdfRect?: [number, number, number, number],
    behavior?: ScrollBehavior,
  ) => void;
}

export function usePdfSearch({
  state,
  pdfUrl,
  displayMode,
  highlightTerms,
  onSearchResults,
  scrollToMatch,
}: UsePdfSearchArgs) {
  const {
    pdfDoc, setSearchResults, setPdfSearchResults, setCurrentResultIndex,
    setCurrentPage, setSearchQuery, setShowSearchBar,
    searchAbortControllerRef, initialSearchPerformedRef, lastHighlightTermsRef,
    initialPageRef, initialPageExplicitRef, preferredSearchPageRef,
  } = state;

  // scale is deliberately absent from the deps: matches are stored in PDF
  // coordinates so zooming must not re-run the search.
  const performSearch = useCallback(async (
    query: string,
    navigateToFirst: boolean = true,
    usePreferredSearchPage: boolean = false,
  ) => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    if (!pdfDoc || !query.trim()) {
      setSearchResults([]);
      setPdfSearchResults([]);
      return;
    }

    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    const pdfResults: PDFSearchResult[] = [];
    const compactQuery = toCompactQuery(query);

    try {
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        if (abortController.signal.aborted) return;

        const page = await pdfDoc.getPage(pageNum);
        if (abortController.signal.aborted) return;

        const textContent = await page.getTextContent();
        if (abortController.signal.aborted) return;

        for (const match of collectPageMatches(textContent.items, compactQuery)) {
          pdfResults.push({ page: pageNum, ...match });
        }
      }

      if (abortController.signal.aborted) return;

      // Base scale keeps the rects external consumers receive scale-independent.
      const baseScale = 1.0;
      const viewportResults: SearchResult[] = await Promise.all(
        pdfResults.map(async (result) => {
          const page = await pdfDoc.getPage(result.page);
          const viewport = page.getViewport({ scale: baseScale });
          const viewRect = viewport.convertToViewportRectangle(result.pdfRect);

          return {
            page: result.page,
            rect: {
              x: Math.min(viewRect[0], viewRect[2]),
              y: Math.min(viewRect[1], viewRect[3]),
              w: Math.abs(viewRect[0] - viewRect[2]),
              h: Math.abs(viewRect[1] - viewRect[3]),
            },
            text: result.text,
          };
        })
      );

      if (abortController.signal.aborted) return;

      // Both arrays are set together; a half-applied update leaves the
      // highlights and the results list disagreeing.
      setPdfSearchResults(pdfResults);
      setSearchResults(viewportResults);
      setCurrentResultIndex(0);
      onSearchResults?.(viewportResults);

      if (navigateToFirst && pdfResults.length > 0) {
        const preferredPage = usePreferredSearchPage ? preferredSearchPageRef.current : undefined;
        const targetIndex = pickPreferredIndex(pdfResults, preferredPage);
        const targetPage = pdfResults[targetIndex].page;
        setCurrentResultIndex(targetIndex);
        setCurrentPage(targetPage);
        if (displayMode === 'scroll') {
          scrollToMatch(targetPage, pdfResults[targetIndex].pdfRect, 'smooth');
        }
      }
    } catch (err) {
      if (!abortController.signal.aborted) {
        console.error('Search error:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [pdfDoc, onSearchResults, displayMode, scrollToMatch]);

  // Initial search driven by the highlightTerms prop, re-run when it changes.
  useEffect(() => {
    const currentTermsString = highlightTerms.join(' ');

    if (currentTermsString !== lastHighlightTermsRef.current) {
      setSearchQuery(currentTermsString);
      lastHighlightTermsRef.current = currentTermsString;
      initialSearchPerformedRef.current = false;

      if (highlightTerms.length === 0) {
        if (searchAbortControllerRef.current) {
          searchAbortControllerRef.current.abort();
          searchAbortControllerRef.current = null;
        }
        setSearchResults([]);
        setPdfSearchResults([]);
        setCurrentResultIndex(0);
      } else {
        setShowSearchBar(true);
      }
    }

    if (highlightTerms.length > 0 && pdfDoc && !initialSearchPerformedRef.current) {
      initialSearchPerformedRef.current = true;
      // An explicit initialPage is the user's page preference from the URL, so
      // the initial search must not navigate away from it.
      const shouldNavigateToFirst = !initialPageExplicitRef.current && initialPageRef.current <= 1;
      performSearch(currentTermsString, shouldNavigateToFirst, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [highlightTerms, pdfDoc, performSearch]);

  // A new document invalidates the previous document's highlights. The search
  // abort lives in the load effect's cleanup so it runs before destruction.
  useEffect(() => {
    initialSearchPerformedRef.current = false;
    lastHighlightTermsRef.current = '';
    setSearchResults([]);
    setPdfSearchResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [pdfUrl]);

  return { performSearch };
}
